import {
    type ComponentRef,
    Injectable,
    inject,
    Injector,
    type OnDestroy,
} from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import type { Observable } from 'rxjs';
import { Subject, filter, map, take, takeUntil } from 'rxjs';
import { ConfirmationDialogHost } from './confirmation/confirmation-dialog-host';
import { DialogDraftState } from './dialog-draft-state';
import type {
    ComponentProperties,
    DialogResult,
    DialogSize,
    ConfirmationConfig,
    ConfirmationOptions,
    QpxDialogConfig,
    QpxOpenDialogOptions,
} from './models';
import { registerDialogLifecycle } from './register-dialog-lifecycle';

interface DialogCloseEvent {
    result: DialogResult;
    shouldClose: boolean;
}

@Injectable()
export class DialogService implements OnDestroy {
    //#region Dependency injection
    private readonly matDialog = inject(MatDialog);
    private readonly injector = inject(Injector);
    private readonly viewport = injectViewportBreakpoints();
    private readonly parent = inject(DialogService, {
        optional: true,
        skipSelf: true,
    });
    //#endregion

    //#region State
    private readonly hasPhoneLayout = this.viewport.atLeast('tablet');
    private readonly hasTabletLayout = this.viewport.atLeast('tablet768');

    private readonly destroy$ = new Subject<void>();

    readonly #closeInternal$ = new Subject<DialogCloseEvent>();
    public readonly _closeInternal$: Observable<DialogCloseEvent> =
        this.#closeInternal$.asObservable();
    //#endregion

    //#region Lifecycle hooks
    ngOnDestroy(): void {
        this.destroy();
    }

    private destroy(): void {
        this.#closeInternal$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
    //#endregion

    //#region Close helpers
    public closeDialog(
        dialogResult: DialogResult = false,
        notifyParent = true
    ): void {
        this.#closeInternal$.next({
            result: dialogResult,
            shouldClose: true,
        });

        if (!notifyParent || !this.parent) return;

        this.parent.#closeInternal$.next({
            result: dialogResult,
            shouldClose: false,
        });
    }
    //#endregion

    //#region Dialog opening methods
    public openDialog<TComponent>(
        options: QpxOpenDialogOptions<TComponent>,
        config: Partial<QpxDialogConfig> = {}
    ): MatDialogRef<TComponent, DialogResult> {
        const {
            disableClose = true,
            restoreFocus = false,
            autoFocus = 'first-heading',
            panelClass,
            size,
            fullscreenBelow = 'tablet768',
            providers = [],
            injector: parentInjector = this.injector,
            ...restOptions
        } = config;

        const childInjector = Injector.create({
            parent: parentInjector,
            providers: [DialogService, DialogDraftState, ...providers],
        });

        const dialogRef = this.matDialog.open<
            TComponent,
            unknown,
            DialogResult
        >(options.component, {
            ...restOptions,
            autoFocus,
            restoreFocus,
            panelClass: this.buildPanelClass(
                panelClass,
                size,
                fullscreenBelow,
                { size: 'md', panelClass: [] }
            ),
            disableClose,
            injector: childInjector,
        });

        if (options.componentInputs && dialogRef.componentRef) {
            this.applyComponentInputs(
                dialogRef.componentRef,
                options.componentInputs
            );
        }

        registerDialogLifecycle(
            dialogRef,
            parentInjector,
            childInjector.get(DialogDraftState)
        );
        const childDialogService = childInjector.get(DialogService);
        childDialogService._closeInternal$
            .pipe(
                filter((event) => event.shouldClose),
                map((event) => event.result),
                takeUntil(dialogRef.afterClosed()),
                takeUntil(this.destroy$)
            )
            .subscribe((result) => {
                dialogRef.close(result);
            });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
                childInjector.destroy();
            });

        return dialogRef;
    }

    public openConfirmationDialog(
        options: ConfirmationOptions,
        config: ConfirmationConfig = {}
    ): MatDialogRef<ConfirmationDialogHost, DialogResult> {
        return this.openConfirmationComponent(
            {
                component: ConfirmationDialogHost,
                componentInputs: { data: options },
            },
            { ariaLabel: options.title || 'Confirmation', ...config }
        );
    }

    public openConfirmationComponent<TComponent>(
        options: QpxOpenDialogOptions<TComponent>,
        config: ConfirmationConfig = {}
    ): MatDialogRef<TComponent, DialogResult> {
        return this.openDialog(options, {
            ...config,
            panelClass: ['confirm', 'auto-h'],
            size: 'sm',
            fullscreenBelow: 'tablet768',
            autoFocus: 'first-tabbable',
            hasBackdrop: true,
            width: '',
            minWidth: '',
            maxWidth: 'var(--mat-dialog-container-max-width)',
            height: '',
            minHeight: '',
            maxHeight: 'calc(100dvh - 32px)',
            position: {},
        });
    }
    //#endregion

    //#region Private methods
    private applyComponentInputs<TComponent>(
        componentRef: ComponentRef<TComponent>,
        componentInputs: Partial<ComponentProperties<TComponent>>
    ): void {
        for (const [name, value] of Object.entries(componentInputs)) {
            if (value === undefined) continue;
            componentRef.setInput(name, value);
        }
    }

    private buildPanelClass(
        panelClass: string | string[] | undefined = [],
        size: DialogSize | undefined,
        fullscreenBelow: 'tablet' | 'tablet768',
        defaults: {
            size: DialogSize;
            panelClass: string | string[];
        } = {
            size: 'md',
            panelClass: [],
        }
    ): string[] {
        const classes: string[] = [];

        // Normalize to array
        const listPanelClasses = Array.isArray(panelClass)
            ? panelClass
            : [panelClass];

        const defaultListPanelClasses = Array.isArray(defaults.panelClass)
            ? defaults.panelClass
            : [defaults.panelClass];

        // Add base class unless 'unstyled' is in panelClass
        if (!listPanelClasses.includes('unstyled')) {
            classes.push('qpx-dialog');
        }

        // Keep the configured size so a mobile-opened dialog can restore it
        // after the viewport widens. CSS owns the live fullscreen breakpoint.
        classes.push(size ?? defaults.size);
        if (fullscreenBelow === 'tablet') classes.push('phone-fullscreen');
        const isFullscreen =
            fullscreenBelow === 'tablet'
                ? !this.hasPhoneLayout()
                : !this.hasTabletLayout();
        if (isFullscreen) classes.push('mobile');

        // Add additional panel classes
        classes.push(...defaultListPanelClasses, ...listPanelClasses);

        return classes;
    }
    //#endregion
}
